import { useCallback } from 'react'

import emDash from '~/components/emdash'
import { logger } from '~/debug/logger'

import { type PowerUnit, powerUnits } from '../types'
import { useNumberFormatter } from './number'
import { type FormatterHook, combineOptions } from './types'
import { useUnitsFormatter } from './units'

function isPowerUnit(unit?: string): unit is PowerUnit {
  return powerUnits.includes(unit as PowerUnit)
}

type PowerOptions = {
  reduce?: boolean
  coercePositive?: boolean
}

const defaultOptions = {
  longForm: false,
  sigFigs: 1,
  units: 'kW',
  omitUnits: false,
  invalidStr: emDash,
  reduce: false,
  coercePositive: false
}

const exportOptions = { ...defaultOptions, omitUnits: true, invalidStr: '' }

export const usePowerFormatter: FormatterHook<PowerOptions> = () => {
  const formatNumber = useNumberFormatter()
  const formatUnits = useUnitsFormatter()

  return useCallback(
    (value, options) => {
      const {
        value: power,
        longForm,
        sigFigs,
        units,
        omitUnits,
        invalidStr,
        reduce,
        coercePositive
      } = combineOptions({
        value,
        options,
        defaults: defaultOptions,
        exports: exportOptions
      })

      // API sometimes returns `K_W`
      const initialUnits = units.toUpperCase().replace('_', '')
      if (!isPowerUnit(initialUnits)) return invalidStr
      if (typeof power !== 'string' && typeof power !== 'number') return invalidStr

      // Coerce to number and force positive if specified
      let powerNum = parseFloat(`${power}`)
      if (isNaN(powerNum)) return invalidStr
      if (coercePositive) powerNum = Math.abs(powerNum)

      // If the options specify to do so, reduce the power to the largest unit that is >= 1
      let powerUnits = initialUnits
      if (reduce) {
        const { num, unit } = reduceWatts(powerNum, powerUnits)
        powerNum = num
        powerUnits = unit
      }

      const powerStr = formatNumber(powerNum, { maximumFractionDigits: sigFigs, export: options?.export })
      if (omitUnits) return powerStr

      const formattedUnits = formatUnits.power(powerUnits, longForm ? 'long' : 'short')
      return [powerStr, formattedUnits].join(' ')
    },
    [formatNumber, formatUnits]
  )
}

type ReduceWattsOutput = {
  num: number
  unit: PowerUnit
}

export function reduceWatts(num: number, unit: PowerUnit = 'W'): ReduceWattsOutput {
  if (Math.abs(num) >= 1000) {
    const nextUnit = powerUnits[powerUnits.indexOf(unit) + 1]
    if (nextUnit) return reduceWatts(num / 1000, nextUnit)
  }
  return { num, unit }
}

export function convertWatts(num: number, currentUnit: PowerUnit, desiredUnit: PowerUnit): number {
  const currentUnitIndex = powerUnits.indexOf(currentUnit)
  const desiredUnitIndex = powerUnits.indexOf(desiredUnit)
  if (currentUnitIndex === -1 || desiredUnitIndex === -1) {
    logger.error(`Invalid Power Unit: Current: ${currentUnit} Desired: ${desiredUnit}`)
    return num
  }

  const diff = desiredUnitIndex - currentUnitIndex
  const convertedNum = num / Math.pow(1000, diff)
  return convertedNum
}
